import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { usePatchMedicalRecordSession } from '@/services';
import {
	PRONTUARIO_SESSIONS_TITLE_CLASS,
	PRONTUARIO_SESSIONS_SUBTITLE_CLASS,
} from '@/pages/professional/sessao-chamada/constants/prontuarioTheme';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/utils/errorMessage';
import {
	findInvalidPbtsFields,
	isValidPbts,
	PBTS_VALIDATION_ERROR,
} from '@/utils/pbts';
import type { ProntuarioSessionPrintField } from '../utils/prontuarioSessionPrint';
import { buildProntuarioSessionPrintFields } from '../utils/buildProntuarioSessionPrintPayload';
import {
	GeneratedSummaryBlock,
	InlineSelectField,
	SessionFieldContainer,
	type ChoiceOption,
	type ProntuarioEditorState,
} from './prontuarioEditableFields';

const ATTENDANCE_OPTIONS: ChoiceOption[] = [
	{ value: 'PRESENT', label: 'Presente' },
	{ value: 'ABSENT_JUSTIFIED', label: 'Faltou (justificado)' },
	{ value: 'ABSENT_UNJUSTIFIED', label: 'Faltou (sem justificativa)' },
];

const RISK_OPTIONS: ChoiceOption[] = [
	{ value: 'NONE', label: 'Nenhum' },
	{ value: 'LOW', label: 'Baixo' },
	{ value: 'MODERATE', label: 'Moderado' },
	{ value: 'HIGH', label: 'Alto' },
];

type FormState = {
	attendance: string;
	observed_mood: string;
	observed_mood_tag: string;
	secondary_complaints: string;
	secondary_complaints_tag: string;
	content_and_techniques: string;
	content_and_techniques_tag: string;
	evolution_notes: string;
	evolution_notes_tag: string;
	risk_indicator: string;
	risk_notes: string;
	risk_notes_tag: string;
	raw_notes: string;
	raw_notes_tag: string;
	summary: string;
	summary_tag: string;
	short_summary: string;
	short_summary_tag: string;
};

const PBTS_FORM_TAG_KEYS = [
	'observed_mood_tag',
	'secondary_complaints_tag',
	'content_and_techniques_tag',
	'evolution_notes_tag',
	'risk_notes_tag',
	'raw_notes_tag',
] as const satisfies ReadonlyArray<keyof FormState>;

type PbtsTagFormKey = (typeof PBTS_FORM_TAG_KEYS)[number];

const buildInitialState = (
	record: MedicalRecords.MedicalRecordSession | null | undefined,
): FormState => ({
	attendance: record?.attendance ?? 'PRESENT',
	observed_mood: record?.observed_mood ?? '',
	observed_mood_tag: record?.observed_mood_tag ?? '',
	secondary_complaints: record?.secondary_complaints ?? '',
	secondary_complaints_tag: record?.secondary_complaints_tag ?? '',
	content_and_techniques: record?.content_and_techniques ?? '',
	content_and_techniques_tag: record?.content_and_techniques_tag ?? '',
	evolution_notes: record?.evolution_notes ?? '',
	evolution_notes_tag: record?.evolution_notes_tag ?? '',
	risk_indicator: record?.risk_indicator ?? 'NONE',
	risk_notes: record?.risk_notes ?? '',
	risk_notes_tag: record?.risk_notes_tag ?? '',
	raw_notes: record?.raw_notes ?? '',
	raw_notes_tag: record?.raw_notes_tag ?? '',
	summary: record?.summary ?? '',
	summary_tag: record?.summary_tag ?? '',
	short_summary: record?.short_summary ?? '',
	short_summary_tag: record?.short_summary_tag ?? '',
});

const areFormStatesEqual = (left: FormState, right: FormState): boolean =>
	(Object.keys(left) as (keyof FormState)[]).every(
		(key) => left[key] === right[key],
	);

const getChangedFormKeys = (
	current: FormState,
	baseline: FormState,
): (keyof FormState)[] =>
	(Object.keys(current) as (keyof FormState)[]).filter(
		(key) => current[key] !== baseline[key],
	);

const buildPartialPatchBody = (
	current: FormState,
	baseline: FormState,
): MedicalRecords.UpdateMedicalRecordSessionBody => {
	const body: MedicalRecords.UpdateMedicalRecordSessionBody = {};

	for (const key of getChangedFormKeys(current, baseline)) {
		body[key] = current[key] as never;
	}

	return body;
};

type ProntuarioSessionEditorState = ProntuarioEditorState & {
	getPrintFields: () => ProntuarioSessionPrintField[];
};

type Props = {
	recordId: string;
	sessionId: string;
	sessionNumber?: number | null;
	dateLabel?: string | null;
	record: MedicalRecords.MedicalRecordSession | null | undefined;
	isLoading?: boolean;
	isError?: boolean;
	variant?: 'dark' | 'light';
	/**
	 * Quando `false`, edições de tags não disparam PATCH inline — o salvamento
	 * acontece só via `save`/`saveAsync`. Default `true` (comportamento do modal).
	 */
	autoSaveTags?: boolean;
	/** Liga a animação de digitação dos valores (no-op por ora). */
	animateValues?: boolean;
	hideSessionMeta?: boolean;
	/** Demo/tutorial: não dispara PATCH na API. */
	demoMode?: boolean;
	onDemoFirstTagEditorOpen?: () => void;
	onDemoTagEditorClose?: () => void;
	onEditorStateChange?: (state: ProntuarioSessionEditorState | null) => void;
};

const ProntuarioEditableSessionDetailView = ({
	recordId,
	sessionId,
	sessionNumber,
	dateLabel,
	record,
	isLoading = false,
	isError = false,
	variant = 'dark',
	autoSaveTags = true,
	animateValues = false,
	hideSessionMeta = false,
	demoMode = false,
	onDemoFirstTagEditorOpen,
	onDemoTagEditorClose,
	onEditorStateChange,
}: Props) => {
	const queryClient = useQueryClient();
	const [form, setForm] = useState<FormState>(() =>
		buildInitialState(record),
	);
	const [baselineForm, setBaselineForm] = useState<FormState>(() =>
		buildInitialState(record),
	);

	useEffect(() => {
		const next = buildInitialState(record);
		setForm(next);
		setBaselineForm(next);
	}, [record]);

	const syncSessionFromServer = useCallback(
		(session: MedicalRecords.MedicalRecordSession) => {
			const next = buildInitialState(session);
			setForm(next);
			setBaselineForm(next);
			queryClient.setQueryData(
				['medical-record-session', recordId, sessionId, 'post-process'],
				session,
			);
		},
		[queryClient, recordId, sessionId],
	);

	const {
		mutate: patchSession,
		mutateAsync: patchSessionAsync,
		isPending,
	} = usePatchMedicalRecordSession(syncSessionFromServer);

	const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
		setForm((prev) => ({ ...prev, [key]: value }));

	const hasChanges = useMemo(
		() => !areFormStatesEqual(form, baselineForm),
		[form, baselineForm],
	);

	const handleTagChange = useCallback(
		(tagKey: PbtsTagFormKey) => (value: string) => {
			if (!isValidPbts(value)) {
				toast.error(PBTS_VALIDATION_ERROR);
				return;
			}

			// Modo "salvar no final": atualiza só o estado local; o PATCH ocorre
			// em save/saveAsync (que já cobre as tags alteradas).
			if (!autoSaveTags) {
				setForm((prev) => ({ ...prev, [tagKey]: value }));
				return;
			}

			const rollbackValue = baselineForm[tagKey];
			setForm((prev) => ({ ...prev, [tagKey]: value }));

			patchSession(
				{ recordId, sessionId, body: { [tagKey]: value } },
				{
					onError: (error) => {
						setForm((prev) => ({
							...prev,
							[tagKey]: rollbackValue,
						}));
						toast.error(
							getErrorMessage(
								error,
								'Erro ao salvar tag. Tente novamente.',
							),
						);
					},
				},
			);
		},
		[autoSaveTags, baselineForm, patchSession, recordId, sessionId],
	);

	const handleSave = useCallback(() => {
		if (demoMode) return;

		const changedKeys = getChangedFormKeys(form, baselineForm);
		if (changedKeys.length === 0) return;

		const changedTagKeys = PBTS_FORM_TAG_KEYS.filter((key) =>
			changedKeys.includes(key),
		);
		const invalidTags = findInvalidPbtsFields(form, changedTagKeys);
		if (invalidTags.length > 0) {
			toast.error(PBTS_VALIDATION_ERROR);
			return;
		}

		const body = buildPartialPatchBody(form, baselineForm);
		if (Object.keys(body).length === 0) return;

		patchSession(
			{ recordId, sessionId, body },
			{
				onSuccess: () =>
					toast.success('Sessão atualizada com sucesso.'),
				onError: (error) =>
					toast.error(
						getErrorMessage(
							error,
							'Erro ao salvar sessão. Tente novamente.',
						),
					),
			},
		);
	}, [baselineForm, demoMode, form, patchSession, recordId, sessionId]);

	// Versão sem toasts e que rejeita em erro — para a revisão pós-sessão.
	const handleSaveAsync = useCallback(async () => {
		if (demoMode) return;

		const changedKeys = getChangedFormKeys(form, baselineForm);
		if (changedKeys.length === 0) return;

		const changedTagKeys = PBTS_FORM_TAG_KEYS.filter((key) =>
			changedKeys.includes(key),
		);
		const invalidTags = findInvalidPbtsFields(form, changedTagKeys);
		if (invalidTags.length > 0) {
			throw new Error(PBTS_VALIDATION_ERROR);
		}

		const body = buildPartialPatchBody(form, baselineForm);
		if (Object.keys(body).length === 0) return;

		await patchSessionAsync({ recordId, sessionId, body });
	}, [baselineForm, demoMode, form, patchSessionAsync, recordId, sessionId]);

	const getPrintFields = useCallback((): ProntuarioSessionPrintField[] => {
		return buildProntuarioSessionPrintFields({
			attendance: form.attendance,
			risk_indicator: form.risk_indicator,
			summary: record?.summary?.trim() || form.summary,
			summary_tag: record?.summary_tag ?? form.summary_tag,
			observed_mood: form.observed_mood,
			observed_mood_tag: form.observed_mood_tag,
			secondary_complaints: form.secondary_complaints,
			secondary_complaints_tag: form.secondary_complaints_tag,
			content_and_techniques: form.content_and_techniques,
			content_and_techniques_tag: form.content_and_techniques_tag,
			evolution_notes: form.evolution_notes,
			evolution_notes_tag: form.evolution_notes_tag,
			risk_notes: form.risk_notes,
			risk_notes_tag: form.risk_notes_tag,
			raw_notes: form.raw_notes,
			raw_notes_tag: form.raw_notes_tag,
		});
	}, [form, record?.summary, record?.summary_tag]);

	useEffect(() => {
		if (isLoading || isError) {
			onEditorStateChange?.(null);
			return;
		}

		onEditorStateChange?.({
			hasChanges,
			isPending,
			save: handleSave,
			saveAsync: handleSaveAsync,
			getPrintFields,
		});
	}, [
		hasChanges,
		isPending,
		handleSave,
		handleSaveAsync,
		getPrintFields,
		isLoading,
		isError,
		onEditorStateChange,
	]);

	const isLight = variant === 'light';

	if (isLoading) {
		return (
			<p
				className={cn(
					'mt-4',
					isLight
						? 'text-sm text-zinc-400'
						: PRONTUARIO_SESSIONS_SUBTITLE_CLASS,
				)}
			>
				Carregando sessão...
			</p>
		);
	}

	if (isError) {
		return (
			<p
				className={cn(
					'mt-4',
					isLight
						? 'text-sm text-zinc-400'
						: PRONTUARIO_SESSIONS_SUBTITLE_CLASS,
				)}
			>
				Não foi possível carregar esta sessão.
			</p>
		);
	}

	const titleClass = isLight
		? 'text-[17px] font-semibold text-zinc-800'
		: PRONTUARIO_SESSIONS_TITLE_CLASS;

	const subtitleClass = isLight
		? 'text-sm text-zinc-400 shrink-0 pt-0.5 text-right'
		: cn(PRONTUARIO_SESSIONS_SUBTITLE_CLASS, 'shrink-0 pt-0.5 text-right');

	return (
		<div className="min-w-0 space-y-5 pb-6">
			{!hideSessionMeta ? (
				<div className="flex items-start justify-between gap-3">
					<h2 className={titleClass}>
						Sessão {sessionNumber ?? '—'}
					</h2>
					{dateLabel ? (
						<p className={subtitleClass}>{dateLabel}</p>
					) : null}
				</div>
			) : null}

			<div
				{...(demoMode
					? { 'data-tutorial-demo': 'review-session-fields' }
					: {})}
				className="space-y-5"
			>
				<div className="flex flex-wrap gap-4">
					<InlineSelectField
						label="Presença"
						value={form.attendance}
						options={ATTENDANCE_OPTIONS}
						onChange={(v) => set('attendance', v)}
						variant={variant}
					/>
					<InlineSelectField
						label="Indicador de risco"
						value={form.risk_indicator}
						options={RISK_OPTIONS}
						onChange={(v) => set('risk_indicator', v)}
						variant={variant}
					/>
				</div>

				{record?.summary ? (
					<GeneratedSummaryBlock
						summary={record.summary}
						summaryTag={record.summary_tag}
						variant={variant}
					/>
				) : null}

				<div className="grid grid-cols-1 gap-3">
					<SessionFieldContainer
						label="Humor observado"
						value={form.observed_mood}
						onChange={(v) => set('observed_mood', v)}
						tagValue={form.observed_mood_tag}
						onTagChange={handleTagChange('observed_mood_tag')}
						placeholder="Descreva o estado emocional observado..."
						rows={4}
						variant={variant}
						animateValue={animateValues}
						{...(demoMode
							? {
									tutorialDemoAddTagTarget:
										'review-first-add-tag',
									tutorialDemoEditorTarget:
										'review-tag-editor',
									onTutorialDemoAddTagOpen:
										onDemoFirstTagEditorOpen,
									onTutorialDemoEditorClose:
										onDemoTagEditorClose,
							  }
							: {})}
					/>

					<SessionFieldContainer
						label="Queixas secundárias"
						value={form.secondary_complaints}
						onChange={(v) => set('secondary_complaints', v)}
						tagValue={form.secondary_complaints_tag}
						onTagChange={handleTagChange(
							'secondary_complaints_tag',
						)}
						placeholder="Queixas trazidas durante a sessão..."
						rows={2}
						variant={variant}
						animateValue={animateValues}
					/>

					<SessionFieldContainer
						label="Conteúdo e técnicas"
						value={form.content_and_techniques}
						onChange={(v) => set('content_and_techniques', v)}
						tagValue={form.content_and_techniques_tag}
						onTagChange={handleTagChange(
							'content_and_techniques_tag',
						)}
						placeholder="Conteúdo trabalhado e técnicas utilizadas..."
						rows={5}
						variant={variant}
						animateValue={animateValues}
					/>

					<SessionFieldContainer
						label="Evolução e observações"
						value={form.evolution_notes}
						onChange={(v) => set('evolution_notes', v)}
						tagValue={form.evolution_notes_tag}
						onTagChange={handleTagChange('evolution_notes_tag')}
						placeholder="Evolução clínica e observações do profissional..."
						rows={5}
						variant={variant}
					/>

					{form.risk_indicator !== 'NONE' ? (
						<SessionFieldContainer
							label="Observações de risco"
							value={form.risk_notes}
							onChange={(v) => set('risk_notes', v)}
							tagValue={form.risk_notes_tag}
							onTagChange={handleTagChange('risk_notes_tag')}
							placeholder="Detalhes sobre o risco identificado..."
							rows={2}
							variant={variant}
							animateValue={animateValues}
						/>
					) : null}

					<SessionFieldContainer
						label="Anotações livres"
						value={form.raw_notes}
						onChange={(v) => set('raw_notes', v)}
						tagValue={form.raw_notes_tag}
						onTagChange={handleTagChange('raw_notes_tag')}
						placeholder="Anotações adicionais da sessão..."
						rows={4}
						variant={variant}
						animateValue={animateValues}
					/>
				</div>
			</div>
		</div>
	);
};

export type { ProntuarioSessionEditorState };
export { ProntuarioEditableSessionDetailView };
